// components/EditComedianteModal.jsx
import React, { useState, useEffect } from 'react';
import { FaTimes, FaImage, FaUser, FaSearch, FaCheck, FaUpload, FaTrash } from 'react-icons/fa';
import { getUsersSinComediante } from '../../../../../components/http/Actions/UsuariosAction';

const EditComedianteModal = ({ comedianteToEdit, setComedianteToEdit, handleEditSubmit, isLoading, onClose }) => {
  const [previewImage, setPreviewImage] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [usuarioActual, setUsuarioActual] = useState(null);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [errorUsuarios, setErrorUsuarios] = useState('');
  const [searchUsuario, setSearchUsuario] = useState('');
  const [usuariosPage, setUsuariosPage] = useState(1);
  const itemsPerPage = 8;
  const token = localStorage.getItem('token');

  // ✅ Cargar usuarios disponibles y asegurar que el usuario actual esté incluido
  useEffect(() => {
    const cargarUsuarios = async () => {
      setLoadingUsuarios(true);
      try {
        await getUsersSinComediante(
          (data) => {
            if (data && data.data) {
              let usuarios = data.data;

              // Crear objeto del usuario actual del comediante
              if (comedianteToEdit?.usuarioNombre && comedianteToEdit?.usuarioEmail) {
                const usuarioActualInfo = {
                  id: comedianteToEdit.usuarioId || comedianteToEdit.applicationUserId,
                  firstName: comedianteToEdit.usuarioNombre.split(' ')[0] || '',
                  lastName: comedianteToEdit.usuarioNombre.split(' ').slice(1).join(' ') || '',
                  email: comedianteToEdit.usuarioEmail,
                  rol: 'Comediante'
                };
                setUsuarioActual(usuarioActualInfo);

                // Si no está en la lista, lo agregamos manualmente
                const yaExiste = usuarios.find(u => u.id === usuarioActualInfo.id);
                if (!yaExiste && usuarioActualInfo.id) {
                  usuarios = [usuarioActualInfo, ...usuarios];
                }
              }

              setUsuariosDisponibles(usuarios);
            }
          },
          setErrorUsuarios,
          1,
          100,
          '',
          token
        );
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
      } finally {
        setLoadingUsuarios(false);
      }
    };
    cargarUsuarios();
  }, [comedianteToEdit, token]);

  // ✅ Asegurar que tenga asignado el ID correcto del usuario actual
  useEffect(() => {
    if (!comedianteToEdit?.applicationUserId && comedianteToEdit?.usuarioId) {
      setComedianteToEdit(prev => ({
        ...prev,
        applicationUserId: comedianteToEdit.usuarioId
      }));
    }
  }, [comedianteToEdit, setComedianteToEdit]);

  // ✅ Imagen: vista previa y prioridad entre archivo / URL
  useEffect(() => {
    if (comedianteToEdit.imagen) {
      setPreviewImage(comedianteToEdit.imagen);
      setSelectedImageFile(null);
    } else if (selectedImageFile) {
      setPreviewImage(URL.createObjectURL(selectedImageFile));
    } else {
      setPreviewImage('');
    }
  }, [comedianteToEdit.imagen, selectedImageFile]);

  // Handlers generales
  const handleChange = (e) => {
    const { name, value } = e.target;
    setComedianteToEdit((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUsuarioSelect = (usuarioId) => {
    setComedianteToEdit((prev) => ({
      ...prev,
      applicationUserId: prev.applicationUserId === usuarioId ? '' : usuarioId,
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      setComedianteToEdit((prev) => ({
        ...prev,
        imagenFile: file,
        imagen: '',
      }));
    }
  };

  const clearImageFile = () => {
    setSelectedImageFile(null);
    setComedianteToEdit((prev) => ({
      ...prev,
      imagenFile: null,
    }));
  };

  const clearImageURL = () => {
    setComedianteToEdit((prev) => ({
      ...prev,
      imagen: '',
    }));
  };

  // Filtrado y paginación
  const filteredUsuarios = usuariosDisponibles.filter((u) => {
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
    const email = u.email.toLowerCase();
    const searchLower = searchUsuario.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const totalPages = Math.ceil(filteredUsuarios.length / itemsPerPage);
  const paginatedUsuarios = filteredUsuarios.slice(
    (usuariosPage - 1) * itemsPerPage,
    usuariosPage * itemsPerPage
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-4xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Editar Comediante</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={(e) =>
            handleEditSubmit(e, { ...comedianteToEdit, imagenFile: selectedImageFile })
          }
          className="space-y-8"
        >
          {/* Nombre */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Nombre</label>
            <input
              type="text"
              name="nombre"
              value={comedianteToEdit.nombre || ''}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
              placeholder="Nombre del comediante"
            />
          </div>

          {/* Imagen */}
          <div className="border-t border-zinc-700 pt-6">
            <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FaImage className="mr-2 text-red-500" />
              Imagen del Comediante
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Subir archivo */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white">Subir archivo</label>
                  {(selectedImageFile || comedianteToEdit.imagenFile) && (
                    <button type="button" onClick={clearImageFile} className="text-red-400 hover:text-red-300 p-1 rounded">
                      <FaTrash className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center ${
                    (selectedImageFile || comedianteToEdit.imagenFile)
                      ? 'border-green-500 bg-green-500/10'
                      : comedianteToEdit.imagen
                      ? 'border-gray-600 bg-gray-500/10'
                      : 'border-zinc-600 hover:border-red-500'
                  }`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="hidden"
                    id="comediante-edit-image-upload"
                    disabled={comedianteToEdit.imagen && comedianteToEdit.imagen.trim() !== ''}
                  />
                  <label
                    htmlFor="comediante-edit-image-upload"
                    className={`cursor-pointer ${comedianteToEdit.imagen ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <FaUpload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-400">Haz clic para subir imagen</p>
                    <p className="text-xs text-gray-500">JPG, PNG, WEBP (máx. 5 MB)</p>
                  </label>
                </div>

                {(selectedImageFile || comedianteToEdit.imagenFile) && (
                  <div className="bg-green-900/20 border border-green-500 rounded-lg p-3">
                    <p className="text-sm text-green-300">
                      <strong>Archivo seleccionado:</strong>{' '}
                      {(selectedImageFile || comedianteToEdit.imagenFile)?.name}
                    </p>
                  </div>
                )}
              </div>

              {/* URL */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white">O ingresar URL</label>
                  {comedianteToEdit.imagen && (
                    <button type="button" onClick={clearImageURL} className="text-red-400 hover:text-red-300 p-1 rounded">
                      <FaTrash className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <input
                  type="url"
                  name="imagen"
                  value={comedianteToEdit.imagen || ''}
                  onChange={handleChange}
                  disabled={selectedImageFile !== null}
                  className={`w-full px-4 py-3 border rounded-lg text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                    selectedImageFile ? 'bg-gray-700 border-gray-600 cursor-not-allowed' : 'bg-zinc-700/50 border-zinc-600'
                  }`}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />

                {previewImage && (
                  <div className="w-full h-40 border border-zinc-600 rounded-lg overflow-hidden">
                    <img src={previewImage} alt="Vista previa" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Biografía */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Biografía</label>
            <textarea
              name="biografia"
              value={comedianteToEdit.biografia || ''}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              placeholder="Escribe una biografía del comediante..."
            ></textarea>
          </div>

          {/* Selector de usuario */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-white flex items-center">
                <FaUser className="mr-2 text-red-500" />
                Usuario Asociado
              </label>
              {comedianteToEdit.applicationUserId && (
                <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                  Usuario seleccionado
                </span>
              )}
            </div>

            {/* Búsqueda */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuario..."
                value={searchUsuario}
                onChange={(e) => {
                  setSearchUsuario(e.target.value);
                  setUsuariosPage(1);
                }}
                className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white text-sm focus:ring-1 focus:ring-red-500"
              />
            </div>

            {/* Lista de usuarios */}
            <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
              {loadingUsuarios ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                </div>
              ) : errorUsuarios ? (
                <div className="p-4 text-red-400 text-sm text-center">
                  Error al cargar usuarios: {errorUsuarios}
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center px-3 py-2 bg-zinc-700/30 border-b border-zinc-700 text-xs text-gray-300">
                    <span>{filteredUsuarios.length} usuarios</span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        disabled={usuariosPage === 1}
                        onClick={() => setUsuariosPage((p) => Math.max(p - 1, 1))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50"
                      >
                        &lt;
                      </button>
                      <span>{usuariosPage} / {totalPages || 1}</span>
                      <button
                        type="button"
                        disabled={usuariosPage >= totalPages}
                        onClick={() => setUsuariosPage((p) => Math.min(p + 1, totalPages))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50"
                      >
                        &gt;
                      </button>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                    {paginatedUsuarios.length === 0 ? (
                      <p className="text-gray-400 text-sm p-4 text-center">
                        {searchUsuario ? 'No se encontraron resultados' : 'No hay usuarios disponibles'}
                      </p>
                    ) : (
                      paginatedUsuarios.map((usuario) => {
                        const isSelected = comedianteToEdit.applicationUserId === usuario.id;
                        const isCurrentUser = usuarioActual && usuarioActual.id === usuario.id;
                        return (
                          <div
                            key={usuario.id}
                            onClick={() => handleUsuarioSelect(usuario.id)}
                            className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'bg-red-900/30 border border-red-600/50'
                                : isCurrentUser
                                ? 'bg-blue-900/20 border border-blue-600/50'
                                : 'hover:bg-zinc-700/50 border border-zinc-700'
                            }`}
                          >
                            <div>
                              <p className="text-sm text-gray-200 font-medium">
                                {usuario.firstName} {usuario.lastName}
                              </p>
                              <p className="text-xs text-gray-400">{usuario.email}</p>
                            </div>
                            <span
                              className={`flex items-center justify-center w-5 h-5 rounded-full ${
                                isSelected ? 'bg-red-600' : 'bg-zinc-700 border border-zinc-600'
                              }`}
                            >
                              {isSelected && <FaCheck className="h-3 w-3 text-white" />}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditComedianteModal;
